import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { resolveCongressCountdownCopy } from "../../domains/congress/congressCountdownCopy";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useNow } from "../../common/hooks/useNow";
import { resolveCongressNames } from "../../domains/congress/congressName";
import {
  deriveCongressCountdown,
  isCongressHeroVisible,
} from "../../domains/congress/deriveCongressCountdown";
import { deriveCongressPhase } from "../../../../api/src/shared/domain/congress-lifecycle";
import { buildCongressDateLabel } from "../../domains/congress/congressDateLabel";
import {
  gaEventCongressCountdownShown,
  gaEventCongressCountdownTapped,
  type CongressAnalyticsContext,
} from "../../tools/analytics/domains/congress.analytics";
import { useApiQuery } from "../../services/api";
import { CongressCard } from "../ui/CongressCard";
import { DEFAULT_COVER_COLOR } from "../../domains/congress/coverColor";

const MINUTE_MS = 60_000;

// Congress spaces the admin flagged as "Promote on the Discovery public app",
// surfaced on the public Discovery feed for users who have joined the linked
// room. Clicking opens that room's Resources filtered to the congress.
export function DiscoveryCongressHero({ enabled }: { enabled: boolean }) {
  const history = useHistory();
  // ONE binding on purpose — see `RoomCongressHero` and the axis rule in
  // `useActiveLang`. Note this hero renders OUTSIDE any room, where no in-room
  // choice exists, so `contentLang` is the fr/en guess: the card is coherent
  // with the corpus the feed shows, which is the property that matters.
  const { contentLang } = useActiveLang();
  const { t } = useTranslation();
  const countdownCopy = resolveCongressCountdownCopy({ contentLang, t });
  const now = useNow(MINUTE_MS);
  const result = useApiQuery(
    "/webapp/private/discovery/congress-spaces",
    enabled ? {} : false
  );

  const cards =
    result.state !== "success"
      ? []
      : result.body
          .map((space) => {
            const countdown = deriveCongressCountdown(space, now);
            // In the feed while the countdown runs, while the congress is live,
            // and through its post-congress grace window — the same window that
            // keeps the room's countdown card up, so the two cannot disagree.
            if (!isCongressHeroVisible(space, now)) return null;
            const names = resolveCongressNames(
              space.translations,
              space.fallbackName,
              contentLang,
              space.city,
              space.country
            );
            return {
              id: space.id,
              roomId: space.roomId,
              name: names.name,
              // Name, place AND date all follow `contentLang` — the card reads as
              // one language or it reads as none. See `useActiveLang`.
              dateLabel: buildCongressDateLabel(
                {
                  startDate: space.startDate,
                  endDate: space.endDate,
                  city: names.city,
                  country: names.country,
                },
                {
                  contentLang,
                  monthStyle: "long",
                  placeSeparator: " | ",
                }
              ),
              coverColor:
                space.coverColor === null
                  ? DEFAULT_COVER_COLOR
                  : space.coverColor,
              coverTextMode: space.coverTextMode,
              cover: space.cover,
              countdown,
              analytics: {
                spaceId: space.id,
                congressName: names.name,
                phase: deriveCongressPhase(space, now),
                roomId: space.roomId,
              } satisfies CongressAnalyticsContext,
            };
          })
          .filter((card) => card !== null);

  // The banner impression is the denominator of the countdown CTR, so it counts
  // banners the feed actually drew — not every render, and not a mounted-but-
  // empty hero.
  useEffectOnce(() => {
    for (const card of cards) {
      gaEventCongressCountdownShown({
        context: card.analytics,
        surface: "discovery_banner",
      });
    }
  }, [cards.map((card) => card.id).join(",")]);

  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 px-5">
      {cards.map((card) => (
        <CongressCard
          key={card.id}
          name={card.name}
          dateLabel={card.dateLabel}
          coverColor={card.coverColor}
          coverTextMode={card.coverTextMode}
          display={{ variant: "full", cover: card.cover }}
          countdown={card.countdown}
          countdownCopy={countdownCopy}
          onClick={() => {
            gaEventCongressCountdownTapped({
              context: card.analytics,
              surface: "discovery_banner",
            });
            history.push({
              pathname: `/room/resources/${card.roomId}`,
              search: `?section=congress:${card.id}`,
              state: { congressEntryPoint: "banner" },
            });
          }}
        />
      ))}
    </div>
  );
}
